/* tutor-1 (C): LA CABECERA DE LA CONVERSACION Y SU LISTA.

   ARCHIVO EN ASCII A PROPOSITO.

   La cabecera: el titulo (Space Grotesk 18) con la flecha que abre la lista, y el boton cuadrado "+". La lista: panel
   flotante de 320 bajo el titulo, "Nueva conversacion" arriba en naranja, y los grupos "Hoy" / "Esta semana" /
   "Antes" por `actualizado` en la hora del aparato; cada renglon con su titulo y "{{n}} mensajes" y su menu
   (Renombrar en linea: Enter guarda, Escape cancela; Borrar con la ventana de confirmar de la casa). tutor-3 (B.5,
   P12): salir del campo (tocar fuera, Tab) tambien guarda; vacio o igual no manda nada.

   tutor-2 remate 2 (S1): el menu de tres puntos de cada renglon se pinta en un PORTAL al `body`, fijo y anclado a su
   boton (a la izquierda, 8 de aire, centrado con el renglon; si no cabe, a la derecha; siempre entero en la ventana),
   para que ninguna caja que se desplaza lo recorte y quede encima de la lista, del panel y de la hoja (z 72: encima
   del velo de las ventanas de la casa, que es 70; la ventana de confirmar de Borrar sale cuando el menu ya se
   cerro). El molde de los menus flotantes de la casa, con sus dos renglones de 44. Se cierra con Escape (solo el
   menu), al tocar fuera, al elegir, al desplazar cualquier cosa y al cambiar el tamano de la ventana; uno a la vez
   (el estado vive en la lista). */
import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { ChevronDown, MoreHorizontal, Plus } from 'lucide-react';
import Ventana, { BOTON_VENTANA_BORRAR, BOTON_VENTANA_SECUNDARIO } from '../alumno/Ventana';
import { AreaDe44 } from '../panel/anatomia';

const FLOTANTE = 'absolute z-[9] bg-[#1B1B1B] border border-[#2C2C2C] rounded-[14px] p-2 max-w-[calc(100%-8px)]';

/** `hoy`, `semana` (los 6 dias anteriores) o `antes`, por la fecha del aparato. */
export function grupoDe(iso, ahora = new Date()) {
  const fecha = new Date(iso);
  if (Number.isNaN(fecha.getTime())) return 'antes';
  const dia = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
  const diferencia = Math.round((dia(ahora) - dia(fecha)) / 86400000);
  if (diferencia <= 0) return 'hoy';
  if (diferencia < 7) return 'semana';
  return 'antes';
}

export const MENU_ANCHO = 176;
export const MENU_ALTO = 98;
const AIRE_DEL_MENU = 8;
const ORILLA_DEL_MENU = 8;

/** Donde va el menu (fijo, en px de la ventana): su borde derecho a 8 del borde izquierdo del boton y centrado con
 *  el boton (que va centrado en su renglon); si no cabe a la izquierda, a la derecha; nunca fuera de la ventana. */
export function posicionDelMenu(boton, ancho, alto, anchoVentana, altoVentana) {
  let left = boton.left - AIRE_DEL_MENU - ancho;
  if (left < ORILLA_DEL_MENU) left = boton.right + AIRE_DEL_MENU;
  if (left + ancho > anchoVentana - ORILLA_DEL_MENU) left = Math.max(ORILLA_DEL_MENU, anchoVentana - ORILLA_DEL_MENU - ancho);
  let top = boton.top + boton.height / 2 - alto / 2;
  top = Math.min(top, altoVentana - ORILLA_DEL_MENU - alto);
  top = Math.max(ORILLA_DEL_MENU, top);
  return { left: Math.round(left), top: Math.round(top) };
}

const OPCION_DEL_MENU_DE_CONVERSACION = 'w-full h-11 px-3 flex items-center text-left rounded-[10px] text-[14px] hover:bg-[#262626] focus:outline-none focus-visible:bg-[#262626]';

function MenuDeConversacion({ botonRef, alRenombrar, alBorrar, alCerrar }) {
  const { t } = useTranslation('study');
  const cajaRef = useRef(null);
  const [pos, setPos] = useState(null);
  const cerrarRef = useRef(alCerrar);
  cerrarRef.current = alCerrar;
  useLayoutEffect(() => {
    const boton = botonRef.current;
    const caja = cajaRef.current;
    if (!boton || !caja) return;
    const medida = caja.getBoundingClientRect();
    setPos(posicionDelMenu(boton.getBoundingClientRect(), medida.width || MENU_ANCHO, medida.height || MENU_ALTO,
      window.innerWidth, window.innerHeight));
  }, [botonRef]);
  useEffect(() => {
    const cerrar = () => cerrarRef.current();
    const tecla = (e) => { if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); cerrar(); } };
    const fuera = (e) => {
      const caja = cajaRef.current;
      const boton = botonRef.current;
      if ((caja && caja.contains(e.target)) || (boton && boton.contains(e.target))) return;
      cerrar();
    };
    const desplazar = (e) => { if (!(cajaRef.current && cajaRef.current.contains(e.target))) cerrar(); };
    window.addEventListener('keydown', tecla, true);
    document.addEventListener('mousedown', fuera, true);
    document.addEventListener('touchstart', fuera, true);
    document.addEventListener('scroll', desplazar, true);
    window.addEventListener('resize', cerrar);
    return () => {
      window.removeEventListener('keydown', tecla, true);
      document.removeEventListener('mousedown', fuera, true);
      document.removeEventListener('touchstart', fuera, true);
      document.removeEventListener('scroll', desplazar, true);
      window.removeEventListener('resize', cerrar);
    };
  }, [botonRef]);
  return createPortal(
    <div ref={cajaRef} role="menu" data-tutor-menu-conversacion="" data-testid="tutor-conversacion-menu-caja"
      className="fixed z-[72] w-44 rounded-[14px] bg-[#1B1B1B] border border-[#2C2C2C] p-1 shadow-[0_16px_40px_rgba(0,0,0,.55)]"
      style={pos ? { left: pos.left, top: pos.top } : { left: 0, top: 0, visibility: 'hidden' }}>
      <button type="button" role="menuitem" className={`${OPCION_DEL_MENU_DE_CONVERSACION} text-[#C8C8C8]`}
        onClick={alRenombrar} data-testid="tutor-conversacion-renombrar">
        {t('tutor.conv.rename')}
      </button>
      <button type="button" role="menuitem" className={`${OPCION_DEL_MENU_DE_CONVERSACION} text-[#F87171]`}
        onClick={alBorrar} data-testid="tutor-conversacion-borrar">
        {t('tutor.conv.delete')}
      </button>
    </div>,
    document.body,
  );
}

function RenglonDeConversacion({ conv, activa, alAbrir, alRenombrar, alBorrar, menu, alAlternarMenu }) {
  const { t } = useTranslation('study');
  const botonRef = useRef(null);
  const [editando, setEditando] = useState(false);
  const [nombre, setNombre] = useState('');
  // tutor-3 (B.5, P12): salir del campo (tocar fuera, Tab) GUARDA como Enter; Escape cancela. La bandera evita que el
  // `blur` que sigue a Enter o a Escape vuelva a decidir. Vacio o igual al de ahora: no se manda nada.
  const decididoRef = useRef(false);
  const titulo = conv.titulo || t('tutor.conv.untitled');
  const guardar = () => {
    const limpio = nombre.trim();
    if (limpio && limpio !== (conv.titulo || '')) alRenombrar(conv.id, limpio);
  };
  if (editando) {
    return (
      <div className="px-2.5 py-1.5">
        <input autoFocus value={nombre} maxLength={60} onChange={(e) => setNombre(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') { e.preventDefault(); decididoRef.current = true; guardar(); setEditando(false); }
            if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); decididoRef.current = true; setEditando(false); }
          }}
          onBlur={() => { if (!decididoRef.current) guardar(); decididoRef.current = false; setEditando(false); }}
          className="w-full h-9 rounded-[10px] bg-[#0E0E0E] border border-[#3a3a3a] px-2.5 text-[14px] text-[#F4F4F3] outline-none"
          data-testid="tutor-renombrar-campo" />
      </div>
    );
  }
  return (
    <div className="relative group">
      <button type="button" onClick={() => alAbrir(conv.id)}
        className={`w-full flex items-center gap-2.5 px-2.5 py-[9px] pr-9 max-md:pr-12 max-md:min-h-[44px] rounded-[10px] text-[14px] text-left hover:bg-[#242424] min-w-0 ${activa ? 'text-[#F4F4F3]' : 'text-[#C8C8C8]'}`}
        data-testid="tutor-conversacion">
        <span className="truncate">{titulo}</span>
        <span className="ml-auto text-[12px] text-[#6F6F6F] whitespace-nowrap">{t('tutor.conv.messages', { n: conv.mensajes || 0 })}</span>
      </button>
      <button ref={botonRef} type="button" onClick={() => alAlternarMenu(conv.id)} aria-label={t('tutor.conv.rename')}
        className="absolute right-1 top-1/2 -translate-y-1/2 w-7 h-7 max-md:w-11 max-md:h-11 rounded-lg flex items-center justify-center text-[#9A9A9A] hover:bg-[#2C2C2C]"
        data-testid="tutor-conversacion-menu">
        <MoreHorizontal className="w-4 h-4" aria-hidden="true" />
      </button>
      {menu ? (
        <MenuDeConversacion botonRef={botonRef} alCerrar={() => alAlternarMenu(null)}
          alRenombrar={() => { alAlternarMenu(null); setNombre(conv.titulo || ''); decididoRef.current = false; setEditando(true); }}
          alBorrar={() => { alAlternarMenu(null); alBorrar(conv.id); }} />
      ) : null}
    </div>
  );
}

function ContenidoDeLaLista({ conversaciones, actual, alAbrir, alNueva, alRenombrar, alBorrar }) {
  const { t } = useTranslation('study');
  // remate 2 (S1): uno a la vez; abrir el de otro renglon cierra el anterior
  const [menuDe, setMenuDe] = useState(null);
  const alternarMenu = (id) => setMenuDe((actualMenu) => (id === null || actualMenu === id ? null : id));
  const grupos = { hoy: [], semana: [], antes: [] };
  (conversaciones || []).forEach((c) => grupos[grupoDe(c.actualizado)].push(c));
  const titulos = { hoy: 'tutor.conv.today', semana: 'tutor.conv.week', antes: 'tutor.conv.earlier' };
  return (
    <>
      <button type="button" onClick={alNueva} className="w-full flex items-center gap-2.5 px-2.5 py-[9px] max-md:min-h-[44px] rounded-[10px] text-[14px] font-semibold text-[#F7A06A] hover:bg-[#242424]"
        data-testid="tutor-nueva-desde-lista">
        <Plus className="w-3.5 h-3.5" aria-hidden="true" />
        <span>{t('tutor.conv.new')}</span>
      </button>
      {['hoy', 'semana', 'antes'].map((g) => (grupos[g].length ? (
        <div key={g} data-testid={`tutor-grupo-${g}`}>
          <div className="text-[11px] tracking-[.08em] uppercase text-[#6F6F6F] font-semibold px-2.5 pt-2.5 pb-1">{t(titulos[g])}</div>
          {grupos[g].map((c) => (
            <RenglonDeConversacion key={c.id} conv={c} activa={c.id === actual} alAbrir={alAbrir} alRenombrar={alRenombrar} alBorrar={alBorrar}
              menu={menuDe === c.id} alAlternarMenu={alternarMenu} />
          ))}
        </div>
      ) : null))}
    </>
  );
}

/* tutor-2 (B.3): abajo de 768 la lista es HOJA desde abajo (`alumno/Ventana`: asa, esquinas de 24, el velo; Esc y el
   velo la cierran), con la MISMA lista adentro y sin botones abajo. Desde 768, el panel flotante de siempre. */
export function ListaDeConversaciones({ enHoja = false, alCerrar, ...resto }) {
  const { t } = useTranslation('study');
  const ref = useRef(null);
  useEffect(() => {
    if (enHoja) return undefined;
    const fuera = (e) => {
      // remate 2 (S1): el menu de un renglon vive en el `body`; tocarlo no es tocar fuera de la lista
      if (e.target && typeof e.target.closest === 'function' && e.target.closest('[data-tutor-menu-conversacion]')) return;
      if (ref.current && !ref.current.contains(e.target)) alCerrar();
    };
    const tecla = (e) => { if (e.key === 'Escape') alCerrar(); };
    document.addEventListener('mousedown', fuera);
    document.addEventListener('keydown', tecla);
    return () => { document.removeEventListener('mousedown', fuera); document.removeEventListener('keydown', tecla); };
  }, [alCerrar, enHoja]);
  if (enHoja) {
    return (
      <Ventana titulo={t('tutor.conv.list')} alCerrar={alCerrar} testid="tutor-conversaciones" etiquetaId="tutor-conversaciones-titulo"
        etiquetaCerrar={t('tutor.mid.close')}>
        <div className="-mx-2.5 pb-6"><ContenidoDeLaLista {...resto} /></div>
      </Ventana>
    );
  }
  return (
    <div ref={ref} className={`${FLOTANTE} top-[50px] left-0 w-[320px] shadow-[0_16px_40px_rgba(0,0,0,.55)] max-h-[60vh] overflow-y-auto`}
      role="dialog" aria-label={t('tutor.conv.list')} data-testid="tutor-conversaciones">
      <ContenidoDeLaLista {...resto} />
    </div>
  );
}

export function VentanaBorrarConversacion({ alConfirmar, alCerrar }) {
  const { t } = useTranslation('study');
  return (
    <Ventana titulo={t('tutor.conv.delete_title')} alCerrar={alCerrar} testid="tutor-borrar-conversacion" etiquetaId="tutor-borrar-titulo"
      botones={(
        <>
          <button type="button" className={BOTON_VENTANA_SECUNDARIO} onClick={alCerrar} data-testid="tutor-borrar-no">{t('common:cancel')}</button>
          <button type="button" className={BOTON_VENTANA_BORRAR} onClick={alConfirmar} data-testid="tutor-borrar-si">{t('tutor.conv.delete')}</button>
        </>
      )}>
      <p className="m-0 text-[14px] leading-[1.55] text-[#C8C8C8]">{t('tutor.conv.delete_body')}</p>
    </Ventana>
  );
}

export default function CabeceraDeConversacion({ titulo, alAbrirLista, alNueva, listaAbierta, children }) {
  const { t } = useTranslation('study');
  return (
    <div className="relative z-[6] flex items-center justify-between gap-3" data-testid="tutor-cabecera">
      <button type="button" onClick={alAbrirLista} aria-expanded={listaAbierta} aria-label={t('tutor.conv.list')}
        className="inline-flex items-center gap-1.5 min-h-[44px] min-w-0 max-w-full rounded-[10px] py-1.5 pr-2 font-['Space_Grotesk'] font-semibold text-[17px] md:text-[18px] tracking-[-.02em] text-[#F4F4F3]"
        data-testid="tutor-titulo">
        <span className="truncate">{titulo || t('tutor.conv.untitled')}</span>
        <ChevronDown className="w-4 h-4 shrink-0 text-[#9A9A9A]" aria-hidden="true" />
      </button>
      <button type="button" onClick={alNueva} aria-label={t('tutor.conv.new')} title={t('tutor.conv.new')}
        className="relative w-10 h-10 shrink-0 rounded-xl bg-[#1B1B1B] border border-[#2C2C2C] text-[#C8C8C8] flex items-center justify-center"
        data-testid="tutor-nueva">
        <AreaDe44 />
        <Plus className="w-4 h-4" aria-hidden="true" />
      </button>
      {children}
    </div>
  );
}
